import { useEffect, useRef, useState } from "react";
import { Bot, Mic, Send, Volume2, VolumeX, X } from "lucide-react";
import api from "../lib/api";
import "./GlobalPartner.css";

const PARTNER_KEY = "focuslinc.partner.history.v1";
const VOICE_KEY = "lincwerks.partner.voice.v1";
const QUICK_PROMPTS = [
  "What needs me most right now?",
  "What should I do next?",
  "What can AI handle without me?",
  "What am I missing?",
];

function loadLocalHistory() {
  try {
    const saved = JSON.parse(localStorage.getItem(PARTNER_KEY) || "[]");
    if (Array.isArray(saved) && saved.length) return saved;
  } catch { /* ignore */ }
  return [{ role: "assistant", content: "I’m here. Talk to me from anywhere in LincWerks." }];
}

export default function GlobalPartner({ surface = "/focus", onNavigate }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(loadLocalHistory);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem(VOICE_KEY) === "on"; } catch { return false; }
  });
  const scroller = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/focus/partner")
      .then(({ data }) => {
        if (!alive || !Array.isArray(data?.messages) || !data.messages.length) return;
        setHistory(data.messages);
      })
      .catch(() => { /* local history remains available */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PARTNER_KEY, JSON.stringify(history.slice(-30))); } catch { /* ignore */ }
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [history, open]);

  useEffect(() => {
    try { localStorage.setItem(VOICE_KEY, voiceOn ? "on" : "off"); } catch { /* ignore */ }
    if (!voiceOn && window.speechSynthesis) window.speechSynthesis.cancel();
  }, [voiceOn]);

  useEffect(() => () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  }, []);

  const speak = (text) => {
    if (!voiceOn || !window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  const send = async (raw) => {
    const message = String(raw || "").trim();
    if (!message || busy) return;
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    const userTurn = { role: "user", content: message };
    const prior = history.slice(-10).map(({ role, content }) => ({ role, content }));
    setHistory((current) => [...current, userTurn]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/focus/partner", { message, history: prior, surface });
      const reply = {
        role: "assistant",
        content: data.reply,
        attention_callout: data.attention_callout,
        actions: data.suggested_actions || [],
        provider: data.provider,
        model: data.model,
      };
      setHistory((current) => [...current, reply]);
      speak(data.reply);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setHistory((current) => [...current, {
        role: "assistant",
        content: typeof detail === "string" ? detail : "I couldn’t reach the LincWerks partner service on that turn.",
      }]);
    } finally {
      setBusy(false);
    }
  };

  const dictate = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) return;
    const recognition = new Recognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      setInput((current) => current ? `${current} ${transcript}` : transcript);
    };
    recognition.start();
  };

  return (
    <>
      <button
        className={`global-partner-launch${open ? " open" : ""}`}
        data-edit-name="AI Partner"
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? "Close LincWerks AI Partner" : "Open LincWerks AI Partner"}
        title="LincWerks AI Partner"
      >
        <Bot size={20} />
        <span>Partner</span>
      </button>

      {open ? (
        <aside className="global-partner-panel" aria-label="LincWerks AI Partner" data-edit-name="AI Partner panel">
          <header className="global-partner-head">
            <div><small>LINCWERKS</small><b>AI Partner</b><span>{surface}</span></div>
            <div className="global-partner-head-actions">
              <button onClick={() => setVoiceOn((value) => !value)} title={voiceOn ? "Voice replies on" : "Voice replies off"} aria-label={voiceOn ? "Turn voice replies off" : "Turn voice replies on"}>
                {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <button onClick={() => setOpen(false)} title="Close" aria-label="Close"><X size={17} /></button>
            </div>
          </header>

          <div className="global-partner-quick">
            {QUICK_PROMPTS.map((prompt) => <button key={prompt} onClick={() => send(prompt)} disabled={busy}>{prompt}</button>)}
          </div>

          <div className="global-partner-history" ref={scroller}>
            {history.map((turn, index) => (
              <article key={`${turn.role}-${index}`} className={`global-partner-turn ${turn.role}`}>
                <small>{turn.role === "assistant" ? "PARTNER" : "YOU"}</small>
                <p>{turn.content}</p>
                {turn.attention_callout ? <div className="global-partner-callout">{turn.attention_callout}</div> : null}
                {turn.actions?.length ? (
                  <div className="global-partner-actions">
                    {turn.actions.map((action, i) => (
                      <button key={`${action.label}-${i}`} disabled={!action.route} onClick={() => action.route && onNavigate?.(action.route)}>{action.label}</button>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
            {busy ? <article className="global-partner-turn assistant"><small>PARTNER</small><p>Working through the live LincWerks state…</p></article> : null}
          </div>

          <form className="global-partner-compose" onSubmit={(event) => { event.preventDefault(); send(input); }}>
            <button type="button" className={listening ? "listening" : ""} onClick={dictate} title="Dictate" aria-label="Dictate"><Mic size={17} /></button>
            <textarea
              rows={2}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Talk or type…"
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send(input);
                }
              }}
            />
            <button type="submit" disabled={!input.trim() || busy} title="Send" aria-label="Send"><Send size={17} /></button>
          </form>
        </aside>
      ) : null}
    </>
  );
}
